import { useState } from 'react';
import { searchApi } from '../../api/searchApi';
import { FaEnvelope, FaTimes, FaSpinner } from 'react-icons/fa';

interface CodeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm?: string;
}

const CodeRequestModal = ({ isOpen, onClose, searchTerm = '' }: CodeRequestModalProps) => {
  const [form, setForm] = useState({
    conditionOrDisease: searchTerm,
    email: '',
    additionalDetails: '',
    codeTypeNeeded: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [resolvedImmediately, setResolvedImmediately] = useState(false); // ✅ New
  const [error, setError] = useState('');

  const codeTypes = ['Any', 'ICD-10-CM', 'ICD-10-PCS', 'HCPCS', 'NDC', 'LOINC', 'SNOMED CT', 'RxNorm'];

  const handleSubmit = async () => {
    setError('');

    if (!form.conditionOrDisease.trim()) {
      setError('Please enter the condition or disease name.');
      return;
    }
    if (!form.email.trim() || !form.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);
      const result = await searchApi.submitCodeRequest({
        conditionOrDisease: form.conditionOrDisease,
        email: form.email,
        additionalDetails: form.additionalDetails,
        codeTypeNeeded: form.codeTypeNeeded || 'Any'
      });

      if (result.success) {
        setTicketId(result.ticketId);
        setResolvedImmediately(result.resolvedImmediately); // ✅ Set karo
        setSubmitted(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {/* @ts-ignore */}
          <FaTimes className="text-xl" />
        </button>

        {/* ✅ SUCCESS STATE */}
        {submitted ? (
          <div className="text-center py-6">

            {resolvedImmediately ? (
              // ✅ NLM se foran mila — email bhej diya
              <>
                <div className="text-5xl mb-4">🎉</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Codes Found & Sent!
                </h2>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <p className="text-green-700 font-medium">
                    ✅ Check your inbox at <strong>{form.email}</strong>
                  </p>
                  <p className="text-green-600 text-sm mt-1">
                    Official codes from National Library of Medicine sent!
                  </p>
                </div>
              </>
            ) : (
              // ❌ NLM se nahi mila — manual review
              <>
                <div className="text-5xl mb-4">📋</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Request Received!
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <p className="text-blue-700 font-medium">
                    Our team will research and email you within{' '}
                    <strong>3-4 business days</strong>
                  </p>
                </div>
              </>
            )}

            {/* Ticket ID — dono cases mein */}
            <div className="bg-gray-50 rounded-lg p-3 mb-6">
              <p className="text-sm text-gray-500">Your Ticket ID</p>
              <p className="text-lg font-bold text-primary-600">{ticketId}</p>
            </div>

            <p className="text-xs text-gray-400 mb-6">
              Confirmation sent to <strong>{form.email}</strong>
            </p>

            <button
              onClick={onClose}
              className="bg-primary-600 text-white px-8 py-2 rounded-full hover:bg-primary-700 transition-colors"
            >
              Close
            </button>
          </div>

        ) : (

          // ✅ FORM STATE
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                {/* @ts-ignore */}
                <FaEnvelope className="text-2xl text-primary-600" />
                <h2 className="text-xl font-bold text-gray-800">Can't Find Your Code?</h2>
              </div>
              <p className="text-gray-500 text-sm">
                Tell us what you need — we'll find the exact code and email it to you.{' '}
                <strong>Free!</strong>
              </p>
            </div>

            {/* Form */}
            <div className="space-y-4">

              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disease / Condition / Procedure{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.conditionOrDisease}
                  onChange={(e) => setForm({ ...form, conditionOrDisease: e.target.value })}
                  placeholder="e.g. Type 2 Diabetes with neuropathy"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-primary-600"
                  style={{ color: 'black' }}
                />
              </div>

              {/* Code Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code Type Needed
                </label>
                <select
                  value={form.codeTypeNeeded}
                  onChange={(e) => setForm({ ...form, codeTypeNeeded: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-primary-600"
                  style={{ color: 'black' }}
                >
                  {codeTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Additional Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Details{' '}
                  <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={form.additionalDetails}
                  onChange={(e) => setForm({ ...form, additionalDetails: e.target.value })}
                  placeholder="Any specific details, patient age, symptoms..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-primary-600 resize-none"
                  style={{ color: 'black' }}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="doctor@hospital.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-primary-600"
                  style={{ color: 'black' }}
                />
              </div>

              {/* Error */}
              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-primary-600 text-white py-3 rounded-full font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    {/* @ts-ignore */}
                    <FaSpinner className="animate-spin" />
                    <span>Searching codes for you...</span>
                  </>
                ) : (
                  <span>🔍 Find My Code — Free</span>
                )}
              </button>

              <p className="text-xs text-center text-gray-400">
                🔒 We never spam. Only one email with your requested codes.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CodeRequestModal;